<template>
  <div class="space-y-3">
    <Head title="Tablero de Comandas | Restaurante y Bares" />

    <!-- ══ HEADER SUPERIOR COMPACTO ══ -->
    <div class="bg-white rounded-2xl p-3 border border-gray-200/80 shadow-xs">
      <div class="flex flex-col md:flex-row md:items-center justify-between gap-2.5">
        <!-- Izquierda: Título y Modo -->
        <div class="flex items-center gap-2.5">
          <div class="w-8 h-8 rounded-xl bg-orange-500 text-white flex items-center justify-center text-sm shadow-xs">
            <i class="fas fa-utensils" />
          </div>
          <div>
            <div class="flex items-center gap-2">
              <h1 class="text-base font-bold text-gray-900 tracking-tight">Tablero de Comandas</h1>
              <span 
                v-if="restaurant_mode" 
                class="px-2 py-0.5 rounded-md text-[10px] font-bold bg-orange-50 text-orange-700 border border-orange-200/60"
              >
                Restaurante / Bar
              </span>
            </div>
            <p class="text-[11px] text-gray-400">
              Vista general en tiempo real de órdenes y mesas
            </p>
          </div>
        </div>

        <!-- Derecha: Tasa, Auto-refresco, Pantalla completa, Nueva Venta -->
        <div class="flex flex-wrap items-center gap-1.5">
          <!-- Tasa -->
          <div class="hidden sm:flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 rounded-lg border border-gray-200/70 text-[11px] font-semibold text-gray-600">
            <span class="text-gray-400">Tasa:</span>
            <span class="text-gray-800 font-bold">1$ = {{ formatNumber(exchange) }} Bs.</span>
          </div>

          <!-- Selector Auto-refresco -->
          <div class="flex items-center bg-gray-50 rounded-lg border border-gray-200/70 p-0.5">
            <button 
              class="px-2 py-1 rounded text-[11px] font-semibold flex items-center gap-1 transition-all" 
              :class="autoRefreshInterval > 0 ? 'bg-orange-50 text-orange-600' : 'text-gray-500 hover:text-gray-700'"
              title="Auto-refrescar pedidos"
              @click="toggleAutoRefresh"
            >
              <i class="fas fa-sync-alt text-[10px]" :class="{'fa-spin': isRefreshing}" />
              <span>{{ autoRefreshInterval > 0 ? `${autoRefreshInterval}s` : 'Pausado' }}</span>
            </button>
            <button 
              class="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-orange-600 rounded transition-colors" 
              title="Refrescar ahora"
              @click="manualRefresh"
            >
              <i class="fas fa-redo text-[10px]" />
            </button>
          </div>

          <!-- Pantalla Completa -->
          <button 
            class="w-8 h-8 bg-gray-50 border border-gray-200/70 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-gray-700 transition-colors flex items-center justify-center" 
            title="Pantalla completa para cocina"
            @click="toggleFullScreen"
          >
            <i class="fas text-xs" :class="isFullScreen ? 'fa-compress' : 'fa-expand'" />
          </button>

          <!-- Botón + POS -->
          <Link 
            href="/pos" 
            class="px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 shadow-xs transition-all active:scale-95"
          >
            <i class="fas fa-plus text-[10px]" />
            <span>Nueva Comanda</span>
          </Link>
        </div>
      </div>

      <!-- Fila de Filtros y Buscador Sutil -->
      <div class="flex flex-col sm:flex-row items-center justify-between gap-2 mt-2.5 pt-2.5 border-t border-gray-100">
        <!-- Filtros de Fecha -->
        <div class="flex items-center gap-1">
          <button 
            class="px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all whitespace-nowrap" 
            :class="filters.date === 'today' ? 'bg-orange-500 text-white shadow-xs' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'"
            @click="setDateFilter('today')"
          >
            Hoy
          </button>
          <button 
            class="px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all whitespace-nowrap" 
            :class="filters.date === 'all_active' ? 'bg-orange-500 text-white shadow-xs' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'"
            @click="setDateFilter('all_active')"
          >
            Todas las Activas
          </button>
          <button 
            class="px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all whitespace-nowrap" 
            :class="filters.date === 'yesterday' ? 'bg-orange-500 text-white shadow-xs' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'"
            @click="setDateFilter('yesterday')"
          >
            Ayer
          </button>
        </div>

        <!-- Buscador Sutil -->
        <div class="relative w-full sm:w-72">
          <i class="fas fa-search absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-[11px]" />
          <input 
            v-model="searchTerm" 
            type="text" 
            placeholder="Buscar mesa, # orden, plato..."
            class="w-full pl-8 pr-7 py-1 bg-gray-50 border border-gray-200/80 rounded-lg text-xs text-gray-800 placeholder-gray-400 focus:bg-white focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 transition-all"
          />
          <button 
            v-if="searchTerm" 
            class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs" 
            @click="searchTerm = ''"
          >
            <i class="fas fa-times-circle" />
          </button>
        </div>
      </div>
    </div>

    <!-- ══ BARRA SLIM DE RESUMEN DE MÉTRICAS (ULTRA COMPACTA) ══ -->
    <div class="grid grid-cols-2 lg:grid-cols-4 gap-2">
      <!-- 1. Por Iniciar -->
      <div class="bg-white rounded-xl px-3 py-1.5 border border-amber-200/80 shadow-xs flex items-center justify-between">
        <div class="flex items-center gap-1.5">
          <span class="w-2 h-2 rounded-full bg-amber-400" />
          <span class="text-[11px] font-semibold text-gray-600">Por iniciar</span>
        </div>
        <div class="flex items-baseline gap-1.5">
          <span class="text-sm font-bold text-gray-900">{{ stats.count_por_iniciar }}</span>
          <span class="text-[11px] text-gray-400">(${{ formatNumber(stats.total_por_iniciar) }})</span>
        </div>
      </div>

      <!-- 2. En Preparación -->
      <div class="bg-white rounded-xl px-3 py-1.5 border border-blue-200/80 shadow-xs flex items-center justify-between">
        <div class="flex items-center gap-1.5">
          <span class="w-2 h-2 rounded-full bg-blue-500" />
          <span class="text-[11px] font-semibold text-gray-600">En preparación</span>
        </div>
        <div class="flex items-baseline gap-1.5">
          <span class="text-sm font-bold text-gray-900">{{ stats.count_en_preparacion }}</span>
          <span class="text-[11px] text-gray-400">(${{ formatNumber(stats.total_en_preparacion) }})</span>
        </div>
      </div>

      <!-- 3. Pendiente por Pago -->
      <div class="bg-white rounded-xl px-3 py-1.5 border border-purple-200/80 shadow-xs flex items-center justify-between">
        <div class="flex items-center gap-1.5">
          <span class="w-2 h-2 rounded-full bg-purple-500" />
          <span class="text-[11px] font-semibold text-gray-600">Pendiente pago</span>
        </div>
        <div class="flex items-baseline gap-1.5">
          <span class="text-sm font-bold text-gray-900">{{ stats.count_pendiente_pago }}</span>
          <span class="text-[11px] text-gray-400">(${{ formatNumber(stats.total_pendiente_pago) }})</span>
        </div>
      </div>

      <!-- 4. Finalizado -->
      <div class="bg-white rounded-xl px-3 py-1.5 border border-emerald-200/80 shadow-xs flex items-center justify-between">
        <div class="flex items-center gap-1.5">
          <span class="w-2 h-2 rounded-full bg-emerald-500" />
          <span class="text-[11px] font-semibold text-gray-600">Finalizadas</span>
        </div>
        <div class="flex items-baseline gap-1.5">
          <span class="text-sm font-bold text-gray-900">{{ stats.count_finalizado }}</span>
          <span class="text-[11px] text-gray-400">(${{ formatNumber(stats.total_finalizado) }})</span>
        </div>
      </div>
    </div>

    <!-- ══ TABLERO KANBAN DE 4 COLUMNAS ══ -->
    <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 items-start">
      <!-- ═════════ COLUMNA 1: POR INICIAR ═════════ -->
      <div 
        class="bg-gray-100/70 rounded-2xl p-2.5 border border-gray-200/70 shadow-xs transition-all min-h-[550px] flex flex-col"
        :class="{'ring-2 ring-amber-400 bg-amber-50/30': dragOverColumn === 'por_iniciar'}"
        @dragover.prevent
        @dragenter.prevent="dragOverColumn = 'por_iniciar'"
        @dragleave="onDragLeave('por_iniciar')"
        @drop="onDrop($event, 'por_iniciar')"
      >
        <!-- Encabezado de Columna -->
        <div class="flex items-center justify-between mb-2 px-1">
          <div class="flex items-center gap-1.5">
            <span class="w-2 h-2 rounded-full bg-amber-500" />
            <h2 class="text-xs font-bold text-gray-800 uppercase tracking-wider">Por iniciar</h2>
            <span class="px-1.5 py-0.2 rounded text-[10px] font-bold bg-amber-100 text-amber-800">
              {{ filteredColumns.por_iniciar.length }}
            </span>
          </div>
          <span class="text-[11px] font-medium text-gray-400">
            ${{ formatNumber(sumColumnTotal(filteredColumns.por_iniciar)) }}
          </span>
        </div>

        <!-- Lista de Comandas -->
        <div class="space-y-2 flex-1">
          <template v-if="filteredColumns.por_iniciar.length > 0">
            <div 
              v-for="order in filteredColumns.por_iniciar" 
              :key="order.id"
              class="bg-white rounded-xl p-3 border border-gray-200/90 shadow-xs hover:shadow-sm transition-all cursor-grab active:cursor-grabbing group relative"
              draggable="true"
              @dragstart="onDragStart($event, order)"
            >
              <!-- Contenido de Tarjeta -->
              <order-card-content :order="order" :exchange="exchange" />

              <!-- Acciones Rápidas Compactas -->
              <div class="mt-2.5 pt-2 border-t border-gray-100 flex items-center justify-between gap-1.5">
                <div class="flex items-center gap-1">
                  <button 
                    class="w-7 h-7 rounded-md bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-500 hover:text-gray-700 flex items-center justify-center text-xs transition-colors" 
                    title="Imprimir ticket térmico"
                    @click="openThermalTicket(order.id)"
                  >
                    <i class="fas fa-print text-[10px]" />
                  </button>
                  <Link 
                    :href="`/pos/${order.id}/show`" 
                    class="w-7 h-7 rounded-md bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-500 hover:text-gray-700 flex items-center justify-center text-xs transition-colors"
                    title="Editar en POS"
                  >
                    <i class="fas fa-edit text-[10px]" />
                  </Link>
                </div>

                <button 
                  class="h-7 px-2.5 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[11px] font-semibold flex items-center gap-1 shadow-xs transition-all active:scale-95 ml-auto" 
                  @click="updateOrderStatus(order.id, 'en_preparacion')"
                >
                  <span>A Cocina</span>
                  <i class="fas fa-arrow-right text-[9px]" />
                </button>
              </div>
            </div>
          </template>

          <div v-else class="h-32 border border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center text-center p-3">
            <span class="text-[11px] text-gray-400">Sin comandas pendientes</span>
          </div>
        </div>
      </div>

      <!-- ═════════ COLUMNA 2: EN PREPARACIÓN ═════════ -->
      <div 
        class="bg-gray-100/70 rounded-2xl p-2.5 border border-gray-200/70 shadow-xs transition-all min-h-[550px] flex flex-col"
        :class="{'ring-2 ring-blue-400 bg-blue-50/30': dragOverColumn === 'en_preparacion'}"
        @dragover.prevent
        @dragenter.prevent="dragOverColumn = 'en_preparacion'"
        @dragleave="onDragLeave('en_preparacion')"
        @drop="onDrop($event, 'en_preparacion')"
      >
        <!-- Encabezado de Columna -->
        <div class="flex items-center justify-between mb-2 px-1">
          <div class="flex items-center gap-1.5">
            <span class="w-2 h-2 rounded-full bg-blue-500" />
            <h2 class="text-xs font-bold text-gray-800 uppercase tracking-wider">En preparación</h2>
            <span class="px-1.5 py-0.2 rounded text-[10px] font-bold bg-blue-100 text-blue-800">
              {{ filteredColumns.en_preparacion.length }}
            </span>
          </div>
          <span class="text-[11px] font-medium text-gray-400">
            ${{ formatNumber(sumColumnTotal(filteredColumns.en_preparacion)) }}
          </span>
        </div>

        <!-- Lista de Comandas -->
        <div class="space-y-2 flex-1">
          <template v-if="filteredColumns.en_preparacion.length > 0">
            <div 
              v-for="order in filteredColumns.en_preparacion" 
              :key="order.id"
              class="bg-white rounded-xl p-3 border border-gray-200/90 shadow-xs hover:shadow-sm transition-all cursor-grab active:cursor-grabbing group relative"
              draggable="true"
              @dragstart="onDragStart($event, order)"
            >
              <!-- Contenido de Tarjeta -->
              <order-card-content :order="order" :exchange="exchange" />

              <!-- Acciones Rápidas Compactas -->
              <div class="mt-2.5 pt-2 border-t border-gray-100 flex items-center justify-between gap-1.5">
                <div class="flex items-center gap-1">
                  <button 
                    class="w-7 h-7 rounded-md bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-400 hover:text-gray-600 flex items-center justify-center text-xs transition-colors" 
                    title="Volver a Por Iniciar"
                    @click="updateOrderStatus(order.id, 'por_iniciar')"
                  >
                    <i class="fas fa-undo text-[10px]" />
                  </button>
                  <button 
                    class="w-7 h-7 rounded-md bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-500 hover:text-gray-700 flex items-center justify-center text-xs transition-colors" 
                    title="Imprimir ticket térmico"
                    @click="openThermalTicket(order.id)"
                  >
                    <i class="fas fa-print text-[10px]" />
                  </button>
                  <Link 
                    :href="`/pos/${order.id}/show`" 
                    class="w-7 h-7 rounded-md bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-500 hover:text-gray-700 flex items-center justify-center text-xs transition-colors"
                    title="Editar en POS"
                  >
                    <i class="fas fa-edit text-[10px]" />
                  </Link>
                </div>

                <button 
                  class="h-7 px-2.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-semibold flex items-center gap-1 shadow-xs transition-all active:scale-95 ml-auto" 
                  @click="updateOrderStatus(order.id, 'pendiente_pago')"
                >
                  <span>A Mesa</span>
                  <i class="fas fa-arrow-right text-[9px]" />
                </button>
              </div>
            </div>
          </template>

          <div v-else class="h-32 border border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center text-center p-3">
            <span class="text-[11px] text-gray-400">Cocina y barra despejadas</span>
          </div>
        </div>
      </div>

      <!-- ═════════ COLUMNA 3: PENDIENTE POR PAGO ═════════ -->
      <div 
        class="bg-gray-100/70 rounded-2xl p-2.5 border border-gray-200/70 shadow-xs transition-all min-h-[550px] flex flex-col"
        :class="{'ring-2 ring-purple-400 bg-purple-50/30': dragOverColumn === 'pendiente_pago'}"
        @dragover.prevent
        @dragenter.prevent="dragOverColumn = 'pendiente_pago'"
        @dragleave="onDragLeave('pendiente_pago')"
        @drop="onDrop($event, 'pendiente_pago')"
      >
        <!-- Encabezado de Columna -->
        <div class="flex items-center justify-between mb-2 px-1">
          <div class="flex items-center gap-1.5">
            <span class="w-2 h-2 rounded-full bg-purple-500" />
            <h2 class="text-xs font-bold text-gray-800 uppercase tracking-wider">Pendiente pago</h2>
            <span class="px-1.5 py-0.2 rounded text-[10px] font-bold bg-purple-100 text-purple-800">
              {{ filteredColumns.pendiente_pago.length }}
            </span>
          </div>
          <span class="text-[11px] font-medium text-gray-400">
            ${{ formatNumber(sumColumnTotal(filteredColumns.pendiente_pago)) }}
          </span>
        </div>

        <!-- Lista de Comandas -->
        <div class="space-y-2 flex-1">
          <template v-if="filteredColumns.pendiente_pago.length > 0">
            <div 
              v-for="order in filteredColumns.pendiente_pago" 
              :key="order.id"
              class="bg-white rounded-xl p-3 border border-gray-200/90 shadow-xs hover:shadow-sm transition-all cursor-grab active:cursor-grabbing group relative"
              draggable="true"
              @dragstart="onDragStart($event, order)"
            >
              <!-- Contenido de Tarjeta -->
              <order-card-content :order="order" :exchange="exchange" />

              <!-- Acciones Rápidas Compactas -->
              <div class="mt-2.5 pt-2 border-t border-gray-100 flex items-center justify-between gap-1.5">
                <div class="flex items-center gap-1">
                  <button 
                    class="w-7 h-7 rounded-md bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-400 hover:text-gray-600 flex items-center justify-center text-xs transition-colors" 
                    title="Volver a Preparación"
                    @click="updateOrderStatus(order.id, 'en_preparacion')"
                  >
                    <i class="fas fa-undo text-[10px]" />
                  </button>
                  <button 
                    class="w-7 h-7 rounded-md bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-500 hover:text-gray-700 flex items-center justify-center text-xs transition-colors" 
                    title="Imprimir cuenta"
                    @click="openThermalTicket(order.id)"
                  >
                    <i class="fas fa-print text-[10px]" />
                  </button>
                  <Link 
                    :href="`/pos/${order.id}/show`" 
                    class="w-7 h-7 rounded-md bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-500 hover:text-gray-700 flex items-center justify-center text-xs transition-colors"
                    title="Cobrar en POS"
                  >
                    <i class="fas fa-cash-register text-[10px]" />
                  </Link>
                </div>

                <button 
                  class="h-7 px-2.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-semibold flex items-center gap-1 shadow-xs transition-all active:scale-95 ml-auto" 
                  @click="updateOrderStatus(order.id, 'finalizado', true)"
                >
                  <span>Finalizar</span>
                  <i class="fas fa-check text-[9px]" />
                </button>
              </div>
            </div>
          </template>

          <div v-else class="h-32 border border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center text-center p-3">
            <span class="text-[11px] text-gray-400">Sin mesas por cobrar</span>
          </div>
        </div>
      </div>

      <!-- ═════════ COLUMNA 4: FINALIZADO ═════════ -->
      <div 
        class="bg-gray-100/70 rounded-2xl p-2.5 border border-gray-200/70 shadow-xs transition-all min-h-[550px] flex flex-col"
        :class="{'ring-2 ring-emerald-400 bg-emerald-50/30': dragOverColumn === 'finalizado'}"
        @dragover.prevent
        @dragenter.prevent="dragOverColumn = 'finalizado'"
        @dragleave="onDragLeave('finalizado')"
        @drop="onDrop($event, 'finalizado')"
      >
        <!-- Encabezado de Columna -->
        <div class="flex items-center justify-between mb-2 px-1">
          <div class="flex items-center gap-1.5">
            <span class="w-2 h-2 rounded-full bg-emerald-500" />
            <h2 class="text-xs font-bold text-gray-800 uppercase tracking-wider">Finalizado</h2>
            <span class="px-1.5 py-0.2 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800">
              {{ filteredColumns.finalizado.length }}
            </span>
          </div>
          <span class="text-[11px] font-medium text-gray-400">
            ${{ formatNumber(sumColumnTotal(filteredColumns.finalizado)) }}
          </span>
        </div>

        <!-- Lista de Comandas -->
        <div class="space-y-2 flex-1">
          <template v-if="filteredColumns.finalizado.length > 0">
            <div 
              v-for="order in filteredColumns.finalizado" 
              :key="order.id"
              class="bg-white rounded-xl p-3 border border-gray-200/90 shadow-xs hover:shadow-sm transition-all cursor-grab active:cursor-grabbing group relative opacity-90 hover:opacity-100"
              draggable="true"
              @dragstart="onDragStart($event, order)"
            >
              <!-- Contenido de Tarjeta -->
              <order-card-content :order="order" :exchange="exchange" />

              <!-- Acciones Rápidas Compactas -->
              <div class="mt-2.5 pt-2 border-t border-gray-100 flex items-center justify-between gap-1.5">
                <div class="flex items-center gap-1">
                  <button 
                    class="w-7 h-7 rounded-md bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-500 hover:text-gray-700 flex items-center justify-center text-xs transition-colors" 
                    title="Imprimir ticket"
                    @click="openThermalTicket(order.id)"
                  >
                    <i class="fas fa-print text-[10px]" />
                  </button>
                  <Link 
                    :href="`/pos/${order.id}/show`" 
                    class="w-7 h-7 rounded-md bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-500 hover:text-gray-700 flex items-center justify-center text-xs transition-colors"
                    title="Ver orden"
                  >
                    <i class="fas fa-eye text-[10px]" />
                  </Link>
                </div>

                <button 
                  class="h-7 px-2 rounded-md bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-600 text-[11px] font-medium flex items-center gap-1 transition-colors ml-auto" 
                  title="Reabrir a Pendiente de Pago"
                  @click="updateOrderStatus(order.id, 'pendiente_pago')"
                >
                  <i class="fas fa-undo text-[9px] text-gray-400" />
                  <span>Reabrir</span>
                </button>
              </div>
            </div>
          </template>

          <div v-else class="h-32 border border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center text-center p-3">
            <span class="text-[11px] text-gray-400">Sin comandas cerradas</span>
          </div>
        </div>
      </div>
    </div>

    <!-- ══ BANNER INFORMATIVO COMPACTO SI MODO RESTAURANTE ESTÁ APAGADO ══ -->
    <div v-if="!restaurant_mode" class="mt-4 bg-orange-50/70 border border-orange-200/80 rounded-2xl p-3 flex flex-col sm:flex-row items-center justify-between gap-3">
      <div class="flex items-center gap-2.5">
        <i class="fas fa-lightbulb text-orange-500 text-base" />
        <p class="text-xs text-orange-950 font-medium">
          Activa el <strong>Modo Restaurante y Bares</strong> en la configuración de tu cuenta para habilitar el flujo nativo de mesas y comandas.
        </p>
      </div>
      <Link 
        :href="`/accounts/${account.id}`" 
        class="px-3 py-1.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-semibold text-xs whitespace-nowrap shadow-xs transition-all"
      >
        Configurar Cuenta
      </Link>
    </div>
  </div>
</template>

<script>
import Layout from '../../Shared/Layout.vue'
import { Head, Link } from '@inertiajs/inertia-vue3'

// Subcomponente estilizado y sutil para la tarjeta de comanda
const OrderCardContent = {
  props: {
    order: Object,
    exchange: Number,
  },
  data() {
    return {
      expanded: false,
    }
  },
  computed: {
    visibleProducts() {
      if (this.expanded || this.order.products.length <= 2) {
        return this.order.products
      }
      return this.order.products.slice(0, 2)
    },
    hiddenCount() {
      return Math.max(0, this.order.products.length - 2)
    },
    isRealNote() {
      return this.order.note && this.order.note.trim() !== 'Compra rápida!' && this.order.note.trim() !== ''
    },
    timeAlertClass() {
      if (this.order.state === 'finalizado') return 'text-gray-400'
      if (this.order.elapsed_minutes > 35) return 'text-rose-600 font-bold'
      if (this.order.elapsed_minutes > 20) return 'text-amber-600 font-medium'
      return 'text-gray-400'
    },
  },
  methods: {
    formatNumber(val) {
      return Number(val || 0).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
  },
  template: `
    <div>
      <!-- Fila 1: Mesa, Orden y Tiempo (Sutil & Limpio) -->
      <div class="flex items-center justify-between gap-1.5 mb-1.5">
        <div class="flex items-center gap-1.5 min-w-0">
          <!-- Badge Mesa -->
          <span class="px-2 py-0.5 rounded text-[11px] font-bold bg-orange-500 text-white shadow-xs truncate max-w-[120px]">
            {{ order.table_name }}
          </span>
          <!-- # Orden -->
          <span class="text-[11px] font-mono text-gray-400">
            #{{ order.number_order }}
          </span>
        </div>

        <!-- Tiempo transcurrido sutil -->
        <span 
          class="text-[10px] whitespace-nowrap flex items-center gap-1 flex-shrink-0"
          :class="timeAlertClass"
          :title="order.created_at"
        >
          <i class="far fa-clock text-[9px]"></i>
          <span>{{ order.human_time }}</span>
        </span>
      </div>

      <!-- Fila 2: Cliente & Mesero -->
      <div class="text-[11px] text-gray-500 mb-2 flex items-center justify-between gap-1">
        <span class="truncate max-w-[140px] text-gray-700 font-medium" :title="order.client_name">
          {{ order.client_name }}
        </span>
        <span class="text-[10px] text-gray-400 truncate max-w-[100px]" :title="order.responsible_name">
          {{ order.responsible_name }}
        </span>
      </div>

      <!-- Nota Especial (Solo si existe nota real de cocina) -->
      <div v-if="isRealNote" class="mb-2 px-2 py-1 rounded bg-amber-50/80 border border-amber-200/60 text-[10px] text-amber-800 flex items-start gap-1 font-medium">
        <i class="fas fa-comment-alt text-[9px] text-amber-500 mt-0.5 flex-shrink-0"></i>
        <span class="line-clamp-2">{{ order.note }}</span>
      </div>

      <!-- Lista Compacta de Platos -->
      <div class="bg-gray-50/80 rounded-lg p-2 mb-2 border border-gray-100">
        <div class="text-[9px] font-bold uppercase tracking-wider text-gray-400 mb-1 flex items-center justify-between">
          <span>Items ({{ order.items_count }})</span>
          <span>{{ order.time_formatted }}</span>
        </div>

        <ul class="space-y-1">
          <li 
            v-for="p in visibleProducts" 
            :key="p.id" 
            class="text-[11px] flex items-center justify-between gap-1.5"
          >
            <div class="flex items-center gap-1.5 min-w-0">
              <span class="w-4 h-4 rounded bg-white border border-gray-200 text-gray-700 text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                {{ p.quantity }}
              </span>
              <span class="text-gray-800 truncate font-medium">{{ p.name }}</span>
            </div>
            <span class="text-[10px] font-medium text-gray-400 flex-shrink-0">
              \${{ formatNumber(p.total) }}
            </span>
          </li>
        </ul>

        <!-- Botón Ver más si hay varios items -->
        <button 
          v-if="order.products.length > 2" 
          @click="expanded = !expanded" 
          type="button"
          class="mt-1 text-[10px] font-semibold text-orange-600 hover:text-orange-700 flex items-center gap-1"
        >
          <span>{{ expanded ? 'Ver menos' : \`+\${hiddenCount} platos más...\` }}</span>
          <i class="fas text-[8px]" :class="expanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
        </button>
      </div>

      <!-- Fila 3: Total y Estado de Pago -->
      <div class="flex items-center justify-between pt-0.5">
        <div>
          <span class="text-xs font-bold text-gray-900">\${{ formatNumber(order.total) }}</span>
          <span class="text-[10px] text-gray-400 font-normal ml-1">({{ formatNumber(order.total_bs) }} Bs)</span>
        </div>

        <div>
          <span 
            v-if="order.is_paid" 
            class="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200/60 inline-flex items-center gap-0.5"
          >
            <i class="fas fa-check text-[8px]"></i> Pagado
          </span>
          <span 
            v-else 
            class="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-purple-50 text-purple-700 border border-purple-200/60 inline-flex items-center"
          >
            Resta \${{ formatNumber(order.pending_balance) }}
          </span>
        </div>
      </div>
    </div>
  `,
}

export default {
  components: {
    Head,
    Link,
    OrderCardContent,
  },
  layout: Layout,
  props: {
    columns: Object,
    stats: Object,
    filters: Object,
    exchange: Number,
    restaurant_mode: Boolean,
    account: Object,
  },
  data() {
    return {
      searchTerm: this.filters?.search || '',
      dragOverColumn: null,
      draggedOrder: null,
      autoRefreshInterval: 20, // cada 20 segundos por defecto
      autoRefreshTimer: null,
      isRefreshing: false,
      isFullScreen: false,
    }
  },
  computed: {
    filteredColumns() {
      if (!this.searchTerm.trim()) {
        return this.columns
      }
      const q = this.searchTerm.toLowerCase().trim()
      const filterList = (list) => {
        return (list || []).filter(item => {
          if (item.table_name && item.table_name.toLowerCase().includes(q)) return true
          if (String(item.number_order).includes(q)) return true
          if (item.client_name && item.client_name.toLowerCase().includes(q)) return true
          if (item.responsible_name && item.responsible_name.toLowerCase().includes(q)) return true
          if (item.note && item.note.toLowerCase().includes(q)) return true
          return item.products.some(p => p.name.toLowerCase().includes(q))
        })
      }

      return {
        por_iniciar: filterList(this.columns.por_iniciar),
        en_preparacion: filterList(this.columns.en_preparacion),
        pendiente_pago: filterList(this.columns.pendiente_pago),
        finalizado: filterList(this.columns.finalizado),
      }
    },
  },
  mounted() {
    this.startAutoRefresh()
    if (typeof document !== 'undefined') {
      document.addEventListener('fullscreenchange', this.onFullScreenChange)
    }
  },
  beforeUnmount() {
    this.stopAutoRefresh()
    if (typeof document !== 'undefined') {
      document.removeEventListener('fullscreenchange', this.onFullScreenChange)
    }
  },
  methods: {
    formatNumber(val) {
      return Number(val || 0).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },

    sumColumnTotal(list) {
      return (list || []).reduce((acc, curr) => acc + (Number(curr.total) || 0), 0)
    },

    setDateFilter(dateOption) {
      this.$inertia.get('/boards', { 
        date: dateOption, 
        search: this.searchTerm, 
      }, {
        preserveState: true,
        preserveScroll: true,
        replace: true,
      })
    },

    manualRefresh() {
      this.isRefreshing = true
      this.$inertia.reload({
        preserveScroll: true,
        preserveState: true,
        onFinish: () => {
          this.isRefreshing = false
        },
      })
    },

    startAutoRefresh() {
      this.stopAutoRefresh()
      if (this.autoRefreshInterval > 0) {
        this.autoRefreshTimer = setInterval(() => {
          this.manualRefresh()
        }, this.autoRefreshInterval * 1000)
      }
    },

    stopAutoRefresh() {
      if (this.autoRefreshTimer) {
        clearInterval(this.autoRefreshTimer)
        this.autoRefreshTimer = null
      }
    },

    toggleAutoRefresh() {
      if (this.autoRefreshInterval === 20) {
        this.autoRefreshInterval = 45
      } else if (this.autoRefreshInterval === 45) {
        this.autoRefreshInterval = 0
      } else {
        this.autoRefreshInterval = 20
      }
      this.startAutoRefresh()
    },

    toggleFullScreen() {
      if (typeof document === 'undefined') return
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch(() => {})
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen().catch(() => {})
        }
      }
    },

    onFullScreenChange() {
      if (typeof document !== 'undefined') {
        this.isFullScreen = !!document.fullscreenElement
      }
    },

    // ══ ACCIÓN: ACTUALIZAR ESTADO DE ORDEN ══
    updateOrderStatus(orderId, newStatus, markAsPaid = false) {
      this.$inertia.post(`/boards/${orderId}/status`, {
        status: newStatus,
        mark_as_paid: markAsPaid,
      }, {
        preserveScroll: true,
        preserveState: true,
      })
    },

    // ══ DRAG AND DROP HANDLERS ══
    onDragStart(event, order) {
      this.draggedOrder = order
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move'
        event.dataTransfer.setData('text/plain', String(order.id))
      }
    },

    onDragLeave(colKey) {
      if (this.dragOverColumn === colKey) {
        this.dragOverColumn = null
      }
    },

    onDrop(event, targetColumn) {
      this.dragOverColumn = null
      if (!this.draggedOrder) return

      const orderId = this.draggedOrder.id
      const currentColumn = this.draggedOrder.state

      if (currentColumn === targetColumn) {
        this.draggedOrder = null
        return
      }

      this.updateOrderStatus(orderId, targetColumn, targetColumn === 'finalizado')
      this.draggedOrder = null
    },

    // ══ IMPRESIÓN DIRECTA DE TICKET TÉRMICO ══
    openThermalTicket(orderId) {
      const url = `/orders/${orderId}/reportThermical`
      const printWindow = window.open(url, '_blank', 'width=450,height=750,toolbar=0,menubar=0,location=0')
      if (printWindow) {
        printWindow.focus()
      }
    },
  },
}
</script>
